// Shared role constants — import from here, never define locally.
// Keeping a single source of truth prevents the MANAGEMENT_ROLES drift bug
// where local copies diverge (e.g. stale "scheduler" role in Travel.jsx).
//
// ⚠️⚠️ THESE ARRAYS ARE LIVE — THEY ARE MUTATED IN PLACE BY `syncRolesFromMe`.
// Roles became creatable in Settings on Sep 10, 2026, so a hardcoded list is
// no longer the truth. Rather than convert ~20 call sites of the shape
// `X.includes(u.role)` / `ALL_ROLES.map(...)` (all evaluated at render time,
// so they pick up a mutation for free), `AuthContext` calls `syncRolesFromMe`
// with the role list `/me` returns and these arrays repopulate.
//
// The literals below are the three built-ins, kept as the INITIAL value so a
// render that happens before /me resolves is identical to what it always was.
//
// ⚠️ Reassigning (`ALL_ROLES = [...]`) would NOT work — every importer holds a
// binding to the original array. Mutate in place; see `_replace` below.
//
// The backend mirror is `auth/roles.py`'s `MANAGEMENT_ROLES`, which does the
// same trick server-side with a DB-derived container object.

// Every role whose base_tier is "management" — the office side.
export const MANAGEMENT_ROLES = ["system_admin", "management"];

// Roles that can be assigned to jobs as Project Manager. Identical to
// MANAGEMENT_ROLES now — any management user is PM-assignable anywhere — but
// kept as its own name because the two ask different questions and could
// diverge again.
export const PM_ROLES = ["system_admin", "management"];

// Every assignable role, in the order the role picker offers them.
export const ALL_ROLES = ["system_admin", "management", "technician"];

// Everyone a purchase order can be assigned to: every role except the field
// side (owner rule, Sep 6, 2026).
// ⚠️ Derived — so it MUST be recomputed inside `syncRolesFromMe`. Left as a
// one-time `ALL_ROLES.filter(...)` at import it would be a stale snapshot of
// the three built-ins forever, silently omitting every custom role.
export const PO_ASSIGNEE_ROLES = ALL_ROLES.filter((r) => r !== "technician");

// Role badge colors — one hue per role, light-theme shades (see the
// status/role color-coding convention in CLAUDE.md). `management` keeps the
// blue `project_manager` used, since it is that role's continuation. A custom
// role's colour is assigned server-side from a palette and arrives on /me.
export const ROLE_COLORS = {
  system_admin: "bg-danger-50 text-danger-700 border-danger-200",
  management:   "bg-blue-50 text-blue-700 border-blue-200",
  technician:   "bg-green-50 text-green-700 border-green-200",
};

export const ROLE_FALLBACK = "bg-zinc-100 text-ink-muted border-zinc-300";

// Display labels — the stored value is a snake_case token.
export const ROLE_LABELS = {
  system_admin: "System Admin",
  management: "Management",
  technician: "Technician",
};

// The Partners role (owner ask, Oct 8, 2026): sees what a technician sees but
// never clocks in or out — its visits advance by the schedule. Recognised by
// key, which is immutable. Backend mirror: PARTNER_ROLE_KEY in auth/roles.py.
export const PARTNER_ROLE_KEY = "partners";

export const roleLabel = (role) =>
  ROLE_LABELS[role] ?? (role ? role.replace(/_/g, " ") : "");

export const roleColor = (role) => ROLE_COLORS[role] ?? ROLE_FALLBACK;

function _replace(target, values) {
  target.length = 0;
  target.push(...values);
}

/**
 * Repopulate the exported arrays and label/colour maps from `/me`'s role list.
 *
 * `roles` is `[{key, label, base_tier, is_system, is_active, color, ...}]`.
 * Called by AuthContext after /me resolves AND after reading the
 * sessionStorage cache, so a reload is never briefly wrong. A missing or empty
 * list is ignored — better the three built-ins than nothing at all.
 */
export function syncRolesFromMe(roles) {
  if (!Array.isArray(roles) || roles.length === 0) return;

  const active = roles.filter((r) => r.is_active !== false);
  const management = active.filter((r) => r.base_tier === "management").map((r) => r.key);

  _replace(MANAGEMENT_ROLES, management);
  _replace(PM_ROLES, management);
  _replace(ALL_ROLES, active.map((r) => r.key));
  // ⚠️ Recomputed, not a stale import-time snapshot.
  _replace(PO_ASSIGNEE_ROLES, active.filter((r) => r.base_tier !== "technician").map((r) => r.key));

  // ⚠️ Every role the server knows about, INCLUDING retired ones, gets a label
  // and colour — a user may still hold a retired role, and their badge must
  // not fall back to a humanized token when the real label exists.
  for (const r of roles) {
    if (r.label) ROLE_LABELS[r.key] = r.label;
    if (r.color) ROLE_COLORS[r.key] = r.color;
  }
}
